import { Link } from 'wouter';
import { Check, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function Pricing() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Start free, scale as you grow. No surprises, no hidden fees.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {/* Free Tier */}
            <div className="bg-white rounded-2xl p-8 border-2 border-slate-200 hover:border-primary-200 transition-colors">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Free</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Perfect for hobbyists and side projects
                </p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-slate-900">$0</span>
                  <span className="text-slate-600">/month</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">3 devices/buckets</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">100k data points/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">7 days retention</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Community support</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Branded widgets</span>
                </li>
              </ul>
              <Link href="/signup">
                <a className="block w-full text-center px-6 py-3 bg-slate-100 text-slate-900 rounded-lg font-semibold hover:bg-slate-200 transition-colors mt-8">
                  Get Started
                </a>
              </Link>
            </div>

            {/* Pro Tier */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-primary-600 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-600 to-secondary-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Pro</h3>
                <div className="mb-2">
                  <span className="text-5xl font-bold text-slate-900">$29</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <p className="text-slate-600">Perfect for small teams and growing projects</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>25 devices</strong>
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">
                    <strong>5M data points/month</strong>
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">90 days retention</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Unbranded widgets</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Natural language queries (100/month)</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Email support</span>
                </li>
              </ul>
              <Link href="/signup">
                <a className="block w-full text-center px-6 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors border-2 border-white mt-8">
                  Start Free Trial
                </a>
              </Link>
            </div>

            {/* Business Tier */}
            <div className="bg-white rounded-2xl p-8 border-2 border-slate-200 hover:border-primary-200 transition-colors">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Business</h3>
                <p className="text-slate-600 text-sm mb-4">
                  For growing companies
                </p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-slate-900">$149</span>
                  <span className="text-slate-600">/month</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">250 devices</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">50M data points/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">1 year retention</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Custom branding</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Priority support</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">99.9% SLA</span>
                </li>
              </ul>
              <Link href="/signup">
                <a className="block w-full text-center px-6 py-3 bg-slate-100 text-slate-900 rounded-lg font-semibold hover:bg-slate-200 transition-colors mt-8">
                  Get Started
                </a>
              </Link>
            </div>

            {/* Enterprise Tier */}
            <div className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-200">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Enterprise</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Custom solutions
                </p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-slate-900">Custom</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Unlimited devices</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Unlimited data</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Custom retention</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">White-label</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">24/7 support</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-secondary-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">99.99% SLA</span>
                </li>
              </ul>
              <a
                href="mailto:sales@dakkio.io"
                className="block w-full text-center px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors mt-8"
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Feature Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Free</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Pro</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Business</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Enterprise</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Devices</td>
                  <td className="py-4 px-4 text-center text-slate-600">3</td>
                  <td className="py-4 px-4 text-center text-slate-600">25</td>
                  <td className="py-4 px-4 text-center text-slate-600">250</td>
                  <td className="py-4 px-4 text-center text-slate-600">Unlimited</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Data Points/Month</td>
                  <td className="py-4 px-4 text-center text-slate-600">100k</td>
                  <td className="py-4 px-4 text-center text-slate-600">5M</td>
                  <td className="py-4 px-4 text-center text-slate-600">50M</td>
                  <td className="py-4 px-4 text-center text-slate-600">Unlimited</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Data Retention</td>
                  <td className="py-4 px-4 text-center text-slate-600">7 days</td>
                  <td className="py-4 px-4 text-center text-slate-600">90 days</td>
                  <td className="py-4 px-4 text-center text-slate-600">1 year</td>
                  <td className="py-4 px-4 text-center text-slate-600">Custom</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Natural Language Queries</td>
                  <td className="py-4 px-4 text-center text-slate-400">—</td>
                  <td className="py-4 px-4 text-center text-secondary-600"><Check className="w-5 h-5 mx-auto" /></td>
                  <td className="py-4 px-4 text-center text-secondary-600"><Check className="w-5 h-5 mx-auto" /></td>
                  <td className="py-4 px-4 text-center text-secondary-600"><Check className="w-5 h-5 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Webhook Notifications</td>
                  <td className="py-4 px-4 text-center text-secondary-600"><Check className="w-5 h-5 mx-auto" /></td>
                  <td className="py-4 px-4 text-center text-secondary-600"><Check className="w-5 h-5 mx-auto" /></td>
                  <td className="py-4 px-4 text-center text-secondary-600"><Check className="w-5 h-5 mx-auto" /></td>
                  <td className="py-4 px-4 text-center text-secondary-600"><Check className="w-5 h-5 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Embeddable Widgets</td>
                  <td className="py-4 px-4 text-center text-slate-600">Branded</td>
                  <td className="py-4 px-4 text-center text-slate-600">Unbranded</td>
                  <td className="py-4 px-4 text-center text-slate-600">Custom</td>
                  <td className="py-4 px-4 text-center text-slate-600">White-label</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">API Rate Limit</td>
                  <td className="py-4 px-4 text-center text-slate-600">100/hr</td>
                  <td className="py-4 px-4 text-center text-slate-600">1k/hr</td>
                  <td className="py-4 px-4 text-center text-slate-600">10k/hr</td>
                  <td className="py-4 px-4 text-center text-slate-600">Custom</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Support</td>
                  <td className="py-4 px-4 text-center text-slate-600">Community</td>
                  <td className="py-4 px-4 text-center text-slate-600">Email</td>
                  <td className="py-4 px-4 text-center text-slate-600">Priority</td>
                  <td className="py-4 px-4 text-center text-slate-600">24/7 Phone</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">SLA Uptime</td>
                  <td className="py-4 px-4 text-center text-slate-400">—</td>
                  <td className="py-4 px-4 text-center text-slate-400">—</td>
                  <td className="py-4 px-4 text-center text-slate-600">99.9%</td>
                  <td className="py-4 px-4 text-center text-slate-600">99.99%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Can I change plans at any time?
              </h3>
              <p className="text-slate-600">
                Yes! Upgrade or downgrade your plan anytime. Changes take effect immediately,
                and we'll prorate your billing.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                What happens if I exceed my data limit?
              </h3>
              <p className="text-slate-600">
                We'll notify you when you approach your limit. You can upgrade your plan or
                purchase additional data points as needed. We never shut off your service
                unexpectedly.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Is there a long-term contract?
              </h3>
              <p className="text-slate-600">
                No contracts required. All plans are month-to-month. Cancel anytime, no
                questions asked. (Enterprise plans may have custom terms.)
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Do you offer discounts for annual billing?
              </h3>
              <p className="text-slate-600">
                Yes! Get 2 months free when you pay annually. That's a 17% discount on Pro
                and Business plans.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-slate-600">
                We accept all major credit cards (Visa, Mastercard, American Express). Enterprise
                customers can also pay via invoice/ACH.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-secondary-500">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Start monitoring your data in minutes. No credit card required for Free plan.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/signup">
              <a className="w-full sm:w-auto px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl flex items-center justify-center">
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Link>
            <a
              href="mailto:sales@dakkio.io"
              className="w-full sm:w-auto px-8 py-4 bg-primary-700 text-white rounded-lg font-semibold hover:bg-primary-800 transition-all border-2 border-white/20"
            >
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
